import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useReferral } from '@/hooks/useReferral';
import { Share2, MessageCircle, Mail, Copy, Users } from 'lucide-react';
import { toast } from 'sonner';

export const ContactShare = () => {
  const { referralCode } = useReferral();

  const referralLink = referralCode 
    ? `${window.location.origin}/auth?ref=${referralCode}`
    : '';

  const shareMessage = `Join me on HiStage! Use my referral code ${referralCode} to get started. ${referralLink}`;

  const handleNativeShare = async () => {
    if (!referralCode) {
      toast.error('Generate a referral code first');
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join HiStage',
          text: shareMessage,
          url: referralLink,
        });
        toast.success('Shared successfully!');
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      navigator.clipboard.writeText(shareMessage);
      toast.success('Message copied to clipboard!');
    }
  };

  const handleSMS = () => {
    if (!referralCode) {
      toast.error('Generate a referral code first');
      return;
    }
    
    const smsUrl = `sms:?body=${encodeURIComponent(shareMessage)}`;
    window.location.href = smsUrl;
  };

  const handleEmail = () => {
    if (!referralCode) {
      toast.error('Generate a referral code first');
      return;
    }
    
    const subject = encodeURIComponent('Join me on HiStage!');
    const body = encodeURIComponent(shareMessage);
    const mailUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailUrl;
  };

  const handleCopyLink = () => {
    if (!referralCode) {
      toast.error('Generate a referral code first');
      return;
    }
    
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied!');
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Your Referral
          </CardTitle>
          <CardDescription>
            Choose how you'd like to share your referral code with friends
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!referralCode ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                Generate a referral code first to start sharing
              </p>
              <Button onClick={() => window.location.href = '/referrals'}>
                Go to Dashboard
              </Button>
            </div>
          ) : (
            <>
              {/* Share Options */}
              <div className="grid gap-3">
                <Button
                  onClick={handleNativeShare}
                  className="w-full justify-start h-auto py-4"
                  variant="outline"
                >
                  <Share2 className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold">Share via Device</div>
                    <div className="text-xs text-muted-foreground">
                      Use your device's native share menu
                    </div>
                  </div>
                </Button>

                <Button
                  onClick={handleSMS}
                  className="w-full justify-start h-auto py-4"
                  variant="outline"
                >
                  <MessageCircle className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold">Share via SMS</div>
                    <div className="text-xs text-muted-foreground">
                      Send a text message to your contacts
                    </div>
                  </div>
                </Button>

                <Button
                  onClick={handleEmail}
                  className="w-full justify-start h-auto py-4"
                  variant="outline"
                >
                  <Mail className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold">Share via Email</div>
                    <div className="text-xs text-muted-foreground">
                      Send an email invitation
                    </div>
                  </div>
                </Button>

                <Button
                  onClick={handleCopyLink}
                  className="w-full justify-start h-auto py-4"
                  variant="outline"
                >
                  <Copy className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold">Copy Link</div>
                    <div className="text-xs text-muted-foreground">
                      Copy the referral link to share manually
                    </div>
                  </div>
                </Button>
              </div>

              {/* Preview */}
              <Card className="bg-muted/50">
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-2">Preview message:</p>
                  <p className="text-sm text-muted-foreground">
                    {shareMessage}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tips for Sharing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
              1
            </div>
            <div>
              <p className="font-medium mb-1">Share on social media</p>
              <p className="text-muted-foreground">Post your referral link on your social profiles</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
              2
            </div>
            <div>
              <p className="font-medium mb-1">Send to friends directly</p>
              <p className="text-muted-foreground">Share via SMS or messaging apps with your contacts</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
              3
            </div>
            <div>
              <p className="font-medium mb-1">Add to your bio</p>
              <p className="text-muted-foreground">Include your referral code in your social media bios</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
